import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx } from './cx';

const control =
  'w-full rounded-input border border-border bg-surface-1 px-3 text-[15px] text-text-1 placeholder:text-text-3 ' +
  'transition duration-150 ease-nolens hover:border-text-3 focus:border-accent focus:outline-none ' +
  'aria-invalid:border-critical disabled:opacity-60';

interface FieldProps {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: (props: { id: string; 'aria-invalid'?: true; 'aria-describedby'?: string }) => ReactNode;
}

/** Label, ajuda e erro ligados por aria (docs/design-system.md §8). */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-text-1">
        {label}
      </label>
      {children({ id, ...(error ? { 'aria-invalid': true } : {}), ...(describedBy ? { 'aria-describedby': describedBy } : {}) })}
      {hint && !error && (
        <p id={hintId} className="text-sm text-text-2">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-sm text-critical">
          {error}
        </p>
      )}
    </div>
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(control, 'h-11', className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx(control, 'min-h-24 py-2.5 leading-relaxed', className)} {...props} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(control, 'h-11 appearance-none pr-8', className)} {...props}>
      {children}
    </select>
  );
}
